import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { formValueSelector, getFormValues } from 'redux-form';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { Action, Resource } from '@topconagriculture/permissions';
import { checkPermissions } from '@topconagriculture/casbin-react';

import api from '../../../core/api';
import {
  addIngredientInventoryStock,
  addIngredientRequest,
  deleteIngredientRequest,
  editIngredientInventory,
  editIngredientRequest,
  editIngredientSettings,
  editIngredientTolerance,
  getAllIngredientsRequest,
  getIngredientDailyUsage,
  getIngredientRequest,
  getIngredientsBatch,
  getIngredientsListRequest,
  renameIngredient,
} from './ingredients.actions';
import { LoadersNames } from '../../ui/ui.constants';
import { selectedSelector } from '../../selectors';
import { getFarmsListRequest } from '../farms/farms.actions';
import { Forms } from '../../form/form.selector';
import { getCodeListFilteredByType, getCodeListFilteredByTypeNormalized } from '../code/code.selector';
import { CodeTypeNames } from '../code/code.types';
import { createPremix, deletePremix } from '../premixes/premixes.actions';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { setSelectedIngredient } from '../../selected/selected.actions';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { getIngredientPrices } from '../dataTools/dataTools.actions';
import { getIngredientDrymatters } from '../../application/ingredients/appIngredients.actions';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';
import { ingredientsRoutesPaths } from '../../../routes/ManageProducts/Ingredients/ingredientsRoutesPaths';
import { getSelectedIngredient } from '../../selected/selected.selector';
import { getIngredientListFiltered, getWaterId } from './ingredients.selector';

export function* ingredientsListFetchSaga() {
  yield put(showLoader(LoadersNames.fetchIngredients));
  try {
    const result = yield call(api.ingredients.getIngredientList);
    yield put(
      getIngredientsListRequest.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchIngredients));
}

export function* fetchAllIngredientsSaga() {
  yield put(showLoader(LoadersNames.fetchIngredients));
  try {
    const result = yield call(api.ingredients.getIngredientList, true);
    yield put(
      getAllIngredientsRequest.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchIngredients));
}

export function* getIngredientSaga(action: any) {
  const ingredientId = action.payload;
  yield put(showLoader(LoadersNames.getIngredient));
  try {
    const result = yield call(api.ingredients.getIngredient, ingredientId);
    yield put(
      getIngredientRequest.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getIngredient));
}

export function* getIngredientsBatchSaga(action: ReturnType<typeof getIngredientsBatch.started>) {
  const ids = action.payload;
  yield put(showLoader(LoadersNames.getIngerdientsBatch));
  try {
    const results = yield all(ids.map((x) => call(api.ingredients.getIngredient, x)));
    yield all(
      results.map((result) =>
        put(
          getIngredientRequest.done({
            params: {},
            result,
          }),
        ),
      ),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getIngerdientsBatch));
}

export function* addIngredientSaga(action: ReturnType<typeof addIngredientRequest.started>) {
  yield put(showLoader(LoadersNames.createIngredient));
  try {
    const formSelector = yield call(getFormValues, Forms.ingredientCreationForm);
    const ingredientCreationFormValues = yield select(formSelector);

    const priceUnitCodes = (yield select(getCodeListFilteredByTypeNormalized))({
      codeType: CodeTypeNames.priceUnitCodes,
      includeInactive: true,
    });

    const { priceUnitCode, refusalLinks: links } = ingredientCreationFormValues;
    const { actualWeight, dryWeight, ...restFormValues } = ingredientCreationFormValues;

    const refusalLinks = links.map((x) => ({
      groupId: x.groupId,
      subtractMode: x.subtractMode,
    }));

    const priceUnitValue: number = priceUnitCodes[priceUnitCode].value1;
    const priceDryToSave = dryWeight / priceUnitValue;

    let payload = {
      ...restFormValues,
      priceDry: priceDryToSave,
      refusalLinks,
      dryMatterPerc: +restFormValues.dryMatterPerc,
    };

    if (action.payload.params) {
      payload = { ...payload, ...action.payload.params };
    }
    const result = yield call(api.ingredients.addIngredient, payload, action.payload);

    yield put(setSelectedIngredient(result.id));
    yield put(
      addIngredientRequest.done({
        params: action.payload,
        result,
      }),
    );
    yield put(notificationSuccess('products:add_ingredient_success'));

    successHandler(action.meta, result);
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.createIngredient));
}

export function* editIngredientSaga(action: ReturnType<typeof editIngredientRequest.started>) {
  const { ingredientId, formName } = action.payload;
  yield put(showLoader(LoadersNames.editIngredient));
  try {
    const ingredientEditFormValues = yield select(getFormValues(Forms.ingredientCreationForm));
    const priceUnitCodes = (yield select(getCodeListFilteredByTypeNormalized))({
      codeType: CodeTypeNames.priceUnitCodes,
      includeInactive: true,
    });
    const { priceUnitCode, refusalLinks: links } = ingredientEditFormValues;
    const {
      actualWeight,
      dryWeight,
      initialDryMatterPerc,
      adjStockOnDM,
      stockDry,
      toleranceWeight,
      ...restFormValues
    } = ingredientEditFormValues;

    const refusalLinks = links.map(({ uuid, ...x }) => ({
      ...x,
      ingredientId,
    }));

    const priceUnitValue: number = priceUnitCodes[priceUnitCode].value1;
    const factor = initialDryMatterPerc / +ingredientEditFormValues.dryMatterPerc;
    // dryWeight === '-' in case of water. below handling of water case
    const priceDryToSave = Number(dryWeight) ? dryWeight / priceUnitValue : actualWeight / priceUnitValue;
    const stockDryToSave = adjStockOnDM ? stockDry : stockDry / factor;

    const ingredientPayload = {
      id: ingredientId,
      ...restFormValues,
      adjStockOnDM,
      priceDry: priceDryToSave,
      stockDry: stockDryToSave,
      toleranceWeight: +toleranceWeight,
      dryMatterPerc: ingredientEditFormValues.dryMatterPerc ? +ingredientEditFormValues.dryMatterPerc : 0,
      refusalLinks,
    };
    let result = yield call(api.ingredients.editIngredient, ingredientId, ingredientPayload, formName);

    yield put(
      editIngredientRequest.done({
        params: action.payload,
        result,
      }),
    );

    result = yield call(api.dataTools.getIngredientPrices, ingredientId);
    yield put(
      getIngredientPrices.done({
        params: ingredientId,
        result,
      }),
    );

    result = yield call(api.ingredients.getIngredientDrymatters, ingredientId);
    yield put(
      getIngredientDrymatters.done({
        params: ingredientId,
        result,
      }),
    );

    yield put(notificationSuccess('products:edit_ingredient_success'));

    successHandler(action.meta);
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.editIngredient));
}

export function* renameIngredientSaga(action: ReturnType<typeof renameIngredient.started>) {
  yield put(showLoader(LoadersNames.renameIngredient));
  try {
    const formName = action.payload;
    const ingredientRenameFormValue = yield select(formValueSelector(Forms.ingredientRenameForm), 'description');

    const ingredientId = yield select(selectedSelector.getSelectedIngredientId);
    const result = yield call(
      api.ingredients.editIngredient,
      ingredientId,
      { description: ingredientRenameFormValue },
      formName,
    );

    yield put(
      editIngredientRequest.done({
        params: { ingredientId, formName },
        result,
      }),
    );
    yield put(notificationSuccess('products:rename_ingredient_success'));
    yield put(push(ingredientsRoutesPaths.general));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.renameIngredient));
}

export function* deleteIngredientSaga(action: ReturnType<typeof deleteIngredientRequest.started>) {
  const ingredientId = yield select(selectedSelector.getSelectedIngredientId);
  const formName = action.payload;
  try {
    yield call(api.ingredients.deleteIngredient, ingredientId, formName);
    yield put(
      deleteIngredientRequest.done({
        params: formName,
        result: ingredientId,
      }),
    );
    const ingredients = yield select(getIngredientListFiltered);
    const firstIngredientId = ingredients[0]?.id;
    yield put(setSelectedIngredient(firstIngredientId));
    yield put(notificationSuccess('products:delete_ingredient_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(push(ingredientsRoutesPaths.general));
}

export function* editIngredientToleranceSaga(action: ReturnType<typeof editIngredientTolerance.started>) {
  yield put(showLoader(LoadersNames.editIngredient));
  try {
    const { ingredientId, formName, toleranceMotion, toleranceWeight, showOnWidget } = action.payload;
    const ingredientPayload = {
      id: ingredientId,
      toleranceMotion,
      toleranceWeight,
      showOnWidget,
    };
    const meta = action.meta;
    const silentSuccess = meta && meta.silentSuccess;
    const result = yield call(api.ingredients.editIngredient, ingredientId, ingredientPayload, formName);

    yield put(
      editIngredientRequest.done({
        params: action.payload,
        result,
      }),
    );

    successHandler(action.meta);

    if (!silentSuccess) {
      yield put(notificationSuccess('products:edit_ingredient_success'));
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.editIngredient));
}

export function* editIngredientSettingsSaga(action: ReturnType<typeof editIngredientSettings.started>) {
  yield put(showLoader(LoadersNames.editIngredient));
  try {
    const { ingredientId, formName } = action.payload;
    const formValues = yield select(getFormValues(Forms.advancedSettings));
    const { loadingAfterFall, loadingAutoStart, loadingId, loadingType } = formValues;
    const ingredientPayload = {
      loadingId,
      loadingType,
      loadingAfterFall,
      loadingAutoStart,
    };
    const meta = action.meta;
    const silentSuccess = meta && meta.silentSuccess;
    const result = yield call(api.ingredients.editIngredient, ingredientId, ingredientPayload, formName);

    yield put(
      editIngredientRequest.done({
        params: action.payload,
        result,
      }),
    );

    successHandler(action.meta);

    if (!silentSuccess) {
      yield put(notificationSuccess('products:edit_ingredient_success'));
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.editIngredient));
}

export function* getIngredientDailyUsageSaga(action: ReturnType<typeof getIngredientDailyUsage.started>) {
  yield put(showLoader(LoadersNames.getIngredientDailyUsage));
  try {
    const canReadInventory = yield call(checkPermissions, {
      resource: Resource.FEED_PRODUCTS_INGREDIENTS_INVENTORY,
      action: Action.READ,
    });
    if (canReadInventory) {
      const result = yield call(api.ingredients.getIngredientDailyUsage, action.payload);
      yield put(
        getIngredientDailyUsage.done({
          params: action.payload,
          result,
        }),
      );
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getIngredientDailyUsage));
}

export function* editIngredientInventorySaga(action: ReturnType<typeof editIngredientInventory.started>) {
  const { ingredientId, formName } = action.payload;
  yield put(showLoader(LoadersNames.editIngredientInventory));
  try {
    const { stock, stockMaxDry, shrinkage, ...ingredientInventoryEditFormValues } = yield select(
      getFormValues(Forms.ingredientInventoryEdit),
    );
    const ingredient = yield select(getSelectedIngredient);
    const waterId = yield select(getWaterId);
    const isWater = ingredient.id === waterId;
    const priceUnitCodes = yield select(getCodeListFilteredByType, {
      codeType: CodeTypeNames.priceUnitCodes,
      includeInactive: true,
    });
    const priceUnitValue = priceUnitCodes[ingredient.priceUnitCode].value1;

    const parsedFormValues = {} as any;

    for (const [key, value] of Object.entries(ingredientInventoryEditFormValues)) {
      parsedFormValues[key] = typeof value === 'string' ? +value.replace(',', '.') : value;
    }

    const { stockDry, stockMax, shrinkageValue, shrinkageTypeCode } = parsedFormValues;

    const ingredientPayload = {
      ...parsedFormValues,
      id: ingredientId,
      stockDry: (isWater ? stock : stockDry) * priceUnitValue,
      stockMax: stockMax * priceUnitValue,
      shrinkageTypeCode: shrinkage ? shrinkageTypeCode : 0,
      shrinkageValue: shrinkage ? shrinkageValue : 0,
    };

    const result = yield call(api.ingredients.editIngredient, ingredientId, ingredientPayload, formName);

    yield put(
      editIngredientRequest.done({
        params: action.payload,
        result,
      }),
    );

    successHandler(action.meta);
    yield put(notificationSuccess('products:edit_ingredient_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.editIngredientInventory));
}

export function* addIngredientInventoryStockSaga(action: ReturnType<typeof addIngredientInventoryStock.started>) {
  const { meta } = action;
  try {
    const result = yield call(
      api.ingredients.addIngredientStock,
      action.payload.ingredientId,
      {
        stock: action.payload.stock,
      },
      action.payload.formName,
    );

    if (meta && meta.onSuccess && typeof meta.onSuccess === 'function') {
      meta.onSuccess();
    }

    yield put(
      addIngredientInventoryStock.done({
        result,
        params: action.payload,
      }),
    );
    yield put(getIngredientRequest.started(action.payload.ingredientId));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* watchIngredientSaga() {
  yield all([
    yield takeLatest(getFarmsListRequest.done, fetchAllIngredientsSaga),
    yield takeLatest(getAllIngredientsRequest.started, fetchAllIngredientsSaga),
    yield takeLatest(createPremix.done, ingredientsListFetchSaga),
    yield takeLatest(deletePremix.done, ingredientsListFetchSaga),
    yield takeLatest(getIngredientRequest.started, getIngredientSaga),
    yield takeLatest(getIngredientsListRequest.started, ingredientsListFetchSaga),
    yield takeLatest(addIngredientRequest.started, addIngredientSaga),
    yield takeLatest(editIngredientRequest.started, editIngredientSaga),
    yield takeLatest(editIngredientTolerance.started, editIngredientToleranceSaga),
    yield takeLatest(renameIngredient.started, renameIngredientSaga),
    yield takeLatest(deleteIngredientRequest.started, deleteIngredientSaga),
    yield takeLatest(getIngredientsBatch.started, getIngredientsBatchSaga),
    yield takeLatest(editIngredientSettings.started, editIngredientSettingsSaga),
    yield takeLatest(getIngredientDailyUsage.started, getIngredientDailyUsageSaga),
    yield takeLatest(editIngredientInventory.started, editIngredientInventorySaga),
    yield takeLatest(addIngredientInventoryStock.started, addIngredientInventoryStockSaga),
  ]);
}
